import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth-context";
import { ImpersonationProvider, useImpersonation } from "@/lib/impersonation-context";
import { ImpersonationBanner } from "@/components/impersonation-banner";
import { initSentry, Sentry } from "@/lib/sentry";
import Layout, { MoreScreen } from "@/components/layout";
import { PublicLayout } from "@/components/public-layout";
import { PublicHeader } from "@/components/public-header";
import { SpectatorSponsorBanner } from "@/components/spectator-sponsor-banner";
import PageTracker from "@/components/page-tracker";
import { initTelemetry } from "@/lib/telemetry";
import { initOfflineSync } from "@/lib/offline/sync";
import { Calendar, LogOut, ShoppingBag } from "lucide-react";
import LoginPage from "@/pages/login";
import RegisterPage from "@/pages/register";
import DashboardPage from "@/pages/dashboard";
import OrganizationsPage from "@/pages/organizations";
import OrganizationCreatePage from "@/pages/organization-create";
import OrganizationEditPage from "@/pages/organization-edit";
import SchoolsPage from "@/pages/schools";
import SchoolCreatePage from "@/pages/school-create";
import SchoolEditPage from "@/pages/school-edit";
import SchoolDetailPage from "@/pages/school-detail";
import PlayersPage from "@/pages/players";
import PlayerCreatePage from "@/pages/player-create";
import PlayerEditPage from "@/pages/player-edit";
import RegistrationIntakePage from "@/pages/registration-intake";
import UnassignedPlayersPage from "@/pages/unassigned-players";
import CoachesPage from "@/pages/coaches";
import CoachDetailPage from "@/pages/coach-detail";
import PlayerImportPage from "@/pages/player-import";
import ParentInvitesPage from "@/pages/parent-invites";
import ParentInviteImportPage from "@/pages/parent-invite-import";
import CourtsPage from "@/pages/courts";
import CourtCreatePage from "@/pages/court-create";
import CourtEditPage from "@/pages/court-edit";
import CourtDetailPage from "@/pages/court-detail";
import AssetDetailPage from "@/pages/asset-detail";
import TeamsPage from "@/pages/teams";
import TeamCreatePage from "@/pages/team-create";
import TeamEditPage from "@/pages/team-edit";
import TeamDetailPage from "@/pages/team-detail";
import AgeGroupDetailPage from "@/pages/age-group-detail";
import AgeGroupEditPage from "@/pages/age-group-edit";
import CourtTemplatesPage from "@/pages/court-templates";
import CourtTemplateCreatePage from "@/pages/court-template-create";
import CourtTemplateEditPage from "@/pages/court-template-edit";
import CourtTemplateDetailPage from "@/pages/court-template-detail";
import MyChildrenPage from "@/pages/my-children";
import PlayerHomePage from "@/pages/player-home";
import MyEventsPage from "@/pages/my-events";
import PackagesPage from "@/pages/packages";
import PackageCreatePage from "@/pages/package-create";
import PackageEditPage from "@/pages/package-edit";
import PackageDetailPage from "@/pages/package-detail";
import CalendarPage from "@/pages/calendar";
import EventsGlobalPage from "@/pages/events-global";
import DiscoverPage from "@/pages/discover";
import ProfilePage from "@/pages/profile";
import VerifyEmailPage from "@/pages/verify-email";
import FamilyAcceptPage from "@/pages/family-accept";
import LinkConfirmPage from "@/pages/link-confirm";
import RegisterLinkPage from "@/pages/register-link";
import SettingsPage from "@/pages/settings";
import AdminEmailsPage from "@/pages/admin-emails";
import AdminPoliciesPage from "@/pages/admin-policies";
import AdminPendingVenuesPage from "@/pages/admin-pending-venues";
import AdminShopProvidersPage from "@/pages/admin-shop-providers";
import AdminVenuesPage from "@/pages/admin-venues";
import AdminAgeGroupsPage from "@/pages/admin-age-groups";
import AdminSportsPage from "@/pages/admin-sports";
import AdminShopProviderEditPage from "@/pages/admin-shop-provider-edit";
import AdminShopProductsPage from "@/pages/admin-shop-products";
import AdminShopOrdersPage from "@/pages/admin-shop-orders";
import AdminShopOrderDetailPage from "@/pages/admin-shop-order-detail";
import OnboardingPage from "@/pages/onboarding";
import PreferencesPage from "@/pages/preferences";
import PolicyPage from "@/pages/policy";
import OffersPage from "@/pages/offers";
import SponsorOffersPage from "@/pages/sponsor-offers";
import ShopPage from "@/pages/shop";
import ShopProviderPage from "@/pages/shop-provider";
import ShopCartPage from "@/pages/shop-cart";
import ShopLinkMockPage from "@/pages/shop-link-mock";
import MyCashbackPage from "@/pages/my-cashback";
import AdminCashbackPage from "@/pages/admin-cashback";
import MyVolunteerOffersPage from "@/pages/my-volunteer-offers";
import SchoolVolunteerDirectoryPage from "@/pages/school-volunteer-directory";
import MyOrdersPage from "@/pages/my-orders";
import MyOrderDetailPage from "@/pages/my-order-detail";
import LockedPage from "@/pages/locked";
import { ConsentGate } from "@/components/consent-gate";
import EventsPage from "@/pages/events";
import EventCreatePage from "@/pages/event-create";
import MatchDayTemplatesPage from "@/pages/match-day-templates";
import EventDetailPage from "@/pages/event-detail";
import EventTabsPage from "@/pages/event-tabs";
import TournamentPage from "@/pages/tournament";
import FixtureInvitesPage from "@/pages/fixture-invites";
import PracticeSeriesPage from "@/pages/practice-series";
import EventEditPage from "@/pages/event-edit";
import MatchCreatePage from "@/pages/match-create";
import MatchDetailPage from "@/pages/match-detail";
import MatchEditPage from "@/pages/match-edit";
import MatchLineupPage from "@/pages/match-lineup";
import BokSmartFormPage from "@/pages/boksmart-form";
import RugbyMatchScorePage from "@/pages/rugby-match-score";
import RugbySevensMatchScorePage from "@/pages/rugby-sevens-match-score";
import NetballMatchScorePage from "@/pages/netball-match-score";
import TennisMatchScorePage from "@/pages/tennis-match-score";
import CricketMatchScorePage from "@/pages/cricket-match-score";
import AthleticsMatchScorePage from "@/pages/athletics-match-score";
import DecathlonMatchScorePage from "@/pages/decathlon-match-score";
import RugbyLeagueTablePage from "@/pages/rugby-league-table";
import NetballLeagueTablePage from "@/pages/netball-league-table";
import TeamPositionsPage from "@/pages/team-positions";
import SponsorsPage from "@/pages/sponsors";
import SponsorCreatePage from "@/pages/sponsor-create";
import SponsorEditPage from "@/pages/sponsor-edit";
import SponsorDetailPage from "@/pages/sponsor-detail";
import SponsorDashboardPage from "@/pages/sponsor-dashboard";
import SponsorUsersPage from "@/pages/sponsor-users";
import PlacementsPage from "@/pages/placements";
import PlacementCreatePage from "@/pages/placement-create";
import PlacementEditPage from "@/pages/placement-edit";
import PlacementDetailPage from "@/pages/placement-detail";
import AssetTypesPage from "@/pages/asset-types";
import AssetTypeCreatePage from "@/pages/asset-type-create";
import AssetTypeEditPage from "@/pages/asset-type-edit";
import AssetTypeDetailPage from "@/pages/asset-type-detail";
import UsersPage from "@/pages/users";
import UserDetailPage from "@/pages/user-detail";
import OrgUsersPage from "@/pages/org-users";
import SchoolUsersPage from "@/pages/school-users";
import MessagesPage from "@/pages/messages";
import InboxPage from "@/pages/inbox";
import NotificationsPage from "@/pages/notifications";
import FollowingPage from "@/pages/following";
import LivePage from "@/pages/live";
import { SpectatorTabBar } from "@/components/spectator-tab-bar";
import ZonesPage from "@/pages/zones";
import ZoneCreatePage from "@/pages/zone-create";
import ZoneEditPage from "@/pages/zone-edit";
import LiveCoachesPage from "@/pages/live-coaches";
import { PinsProvider } from "@/lib/pins-context";
import { FavoritesProvider } from "@/lib/favorites-context";
import { Fragment, lazy, Suspense, useLayoutEffect, useRef, type ReactNode } from "react";

// Code-split: three.js is ~650KB gzipped, only load when user visits 3D view
const Field3DPage = lazy(() => import("@/pages/field-3d"));

// The app is ALWAYS viewing as some effective user -- yourself in the common
// case, or someone you've switched to impersonating. UserScope makes a switch
// behave like a logout/login: keying the whole user-scoped subtree (pins,
// favorites, and every route/page) on the effective user id tears it all down
// and rebuilds it for the new user, so no state bleeds across. The auth +
// impersonation providers live ABOVE this boundary, so the real superuser
// identity and the persona switcher persist across switches.
function UserScope({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  return (
    <Fragment key={user?.user.id ?? "anon"}>
      <PinsProvider>
        <FavoritesProvider>{children}</FavoritesProvider>
      </PinsProvider>
    </Fragment>
  );
}

// On a genuine switch (effective user changed, not initial load / deep-link),
// land on the entry point "/" -- the same place a fresh login lands, which
// RequireAuth then routes per the new user's roles. Lives above UserScope so its
// ref survives the subtree remount.
function ImpersonationReset() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const prevUserId = useRef<string | null>(null);
  // Layout effect so the redirect happens before paint -- avoids a one-frame
  // flash of the previous persona's (possibly restricted) route.
  useLayoutEffect(() => {
    const uid = user?.user.id ?? null;
    if (uid && prevUserId.current !== null && prevUserId.current !== uid) {
      navigate("/", { replace: true });
    }
    prevUserId.current = uid;
  }, [user?.user.id, navigate]);
  return null;
}

function MinimalLayout({ children }: { children: ReactNode }) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-background">
      <PublicHeader
        navItems={[
          { label: "Events", icon: Calendar, onClick: () => navigate("/discover"), dataAction: "nav-events" },
          { label: "Shop", icon: ShoppingBag, onClick: () => navigate("/shop"), dataAction: "nav-shop" },
          { label: "My Profile", onClick: () => navigate("/profile"), dataAction: "nav-profile" },
        ]}
        actions={[
          { label: "Sign out", icon: LogOut, onClick: () => { logout(); navigate("/login"); }, dataAction: "sign-out" },
        ]}
      />
      {/* Always present so an impersonated public/no-role user can still exit
          impersonation -- this layout has no sidebar (where the other Exit lives). */}
      <ImpersonationBanner />
      <SpectatorSponsorBanner />
      {/* 202: reserve space for the fixed spectator tab bar below md. */}
      <main className="p-4 pb-24 md:p-6 md:pb-6">{children}</main>
      <SpectatorTabBar />
    </div>
  );
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading, lockedReason } = useAuth();
  const { isImpersonating } = useImpersonation();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (lockedReason && location.pathname !== "/locked") {
    return <Navigate to="/locked" replace />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // First-time users: redirect to onboarding wizard (073). The wizard is
  // parent/spectator-centric (follow schools, sports, age groups, notifications)
  // so it only runs for users whose roles are all spectator-scoped -- or who
  // have no roles at all. Coaches, school admins, sponsor admins, etc. skip it.
  const SPECTATOR_ROLES = new Set(["parent", "super_parent", "player"]);
  const isSpectatorOnly =
    user.roles.length === 0 ||
    user.roles.every((r) => SPECTATOR_ROLES.has(r.role_type));
  // Don't force onboarding while impersonating -- the admin isn't the user and
  // the onboarding page has no chrome, which would strip the Exit control.
  if (!user.user.onboarding_completed_at && isSpectatorOnly && !isImpersonating && location.pathname !== "/onboarding") {
    return <Navigate to="/onboarding" replace />;
  }

  // Onboarding page renders without layout
  if (location.pathname === "/onboarding") {
    return <ConsentGate>{children}</ConsentGate>;
  }

  // Unaffiliated users (no roles) get minimal layout, redirect / to /discover
  if (user.roles.length === 0) {
    if (location.pathname === "/") {
      return <Navigate to="/discover" replace />;
    }
    return (
      <ConsentGate>
        <MinimalLayout>{children}</MinimalLayout>
      </ConsentGate>
    );
  }

  return (
    <ConsentGate>
      <Layout>{children}</Layout>
    </ConsentGate>
  );
}

// 102: PublicOrAuth lets a route be reachable both by anonymous users
// (rendered inside PublicLayout) AND by authenticated users (delegated to
// the existing RequireAuth chain, which picks MinimalLayout vs Layout
// based on roles). Currently used only for /discover -- the explicit
// anonymous entry point per BA row 101. Other routes remain
// authenticated-only via RequireAuth or guest-only via RequireGuest.
function PublicOrAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!user) {
    return <PublicLayout>{children}</PublicLayout>;
  }

  return <RequireAuth>{children}</RequireAuth>;
}

function RequireGuest({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

// Initialize telemetry and Sentry once at module load
initTelemetry();
initSentry();
// Offline Support (120): drain queued score writes on load + on reconnect.
initOfflineSync();

export default function App() {
  return (
    <Sentry.ErrorBoundary fallback={<p className="p-8 text-muted-foreground">An unexpected error occurred. Please refresh.</p>}>
    <BrowserRouter>
      <PageTracker />
      <AuthProvider>
        <ImpersonationProvider>
        <ImpersonationReset />
        <UserScope>
        <Routes>
          <Route path="/offers" element={<OffersPage />} />
          <Route path="/locked" element={<LockedPage />} />
          <Route path="/privacy" element={<PolicyPage type="privacy" title="Privacy Policy" />} />
          <Route path="/terms" element={<PolicyPage type="terms" title="Terms of Service" />} />
          <Route path="/families-policy" element={<PolicyPage type="families" title="Families Policy" />} />
          <Route
            path="/login"
            element={
              <RequireGuest>
                <LoginPage />
              </RequireGuest>
            }
          />
          <Route
            path="/register"
            element={
              <RequireGuest>
                <RegisterPage />
              </RequireGuest>
            }
          />
          <Route
            path="/verify-email/:token"
            element={<VerifyEmailPage />}
          />
          <Route
            path="/family/accept/:token"
            element={<FamilyAcceptPage />}
          />
          <Route
            path="/link-confirm"
            element={<LinkConfirmPage />}
          />
          <Route
            path="/register-link"
            element={<RegisterLinkPage />}
          />
          <Route
            path="/"
            element={
              <RequireAuth>
                <DashboardPage />
              </RequireAuth>
            }
          />
          <Route
            path="/users"
            element={
              <RequireAuth>
                <UsersPage />
              </RequireAuth>
            }
          />
          <Route
            path="/users/:id"
            element={
              <RequireAuth>
                <UserDetailPage />
              </RequireAuth>
            }
          />
          <Route
            path="/organizations/:id/users"
            element={
              <RequireAuth>
                <OrgUsersPage />
              </RequireAuth>
            }
          />
          <Route
            path="/schools/:id/users"
            element={
              <RequireAuth>
                <SchoolUsersPage />
              </RequireAuth>
            }
          />
          <Route
            path="/organizations"
            element={
              <RequireAuth>
                <OrganizationsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/organizations/new"
            element={
              <RequireAuth>
                <OrganizationCreatePage />
              </RequireAuth>
            }
          />
          <Route
            path="/organizations/:id/edit"
            element={
              <RequireAuth>
                <OrganizationEditPage />
              </RequireAuth>
            }
          />
          <Route
            path="/schools"
            element={
              <RequireAuth>
                <SchoolsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/schools/new"
            element={
              <RequireAuth>
                <SchoolCreatePage />
              </RequireAuth>
            }
          />
          <Route
            path="/schools/:id/edit"
            element={
              <RequireAuth>
                <SchoolEditPage />
              </RequireAuth>
            }
          />
          <Route
            path="/schools/:id"
            element={
              <RequireAuth>
                <SchoolDetailPage />
              </RequireAuth>
            }
          />
          <Route
            path="/players"
            element={
              <RequireAuth>
                <PlayersPage />
              </RequireAuth>
            }
          />
          <Route
            path="/schools/:schoolId/players/new"
            element={
              <RequireAuth>
                <PlayerCreatePage />
              </RequireAuth>
            }
          />
          <Route
            path="/players/:id/edit"
            element={
              <RequireAuth>
                <PlayerEditPage />
              </RequireAuth>
            }
          />
          <Route
            path="/schools/:schoolId/players/import"
            element={
              <RequireAuth>
                <PlayerImportPage />
              </RequireAuth>
            }
          />
          <Route
            path="/schools/:schoolId/register-player"
            element={
              <RequireAuth>
                <RegistrationIntakePage />
              </RequireAuth>
            }
          />
          <Route
            path="/schools/:schoolId/players/unassigned"
            element={
              <RequireAuth>
                <UnassignedPlayersPage />
              </RequireAuth>
            }
          />
          <Route
            path="/coaches"
            element={
              <RequireAuth>
                <CoachesPage />
              </RequireAuth>
            }
          />
          <Route
            path="/coaches/:id"
            element={
              <RequireAuth>
                <CoachDetailPage />
              </RequireAuth>
            }
          />
          <Route
            path="/parent-invites"
            element={
              <RequireAuth>
                <ParentInvitesPage />
              </RequireAuth>
            }
          />
          <Route
            path="/schools/:schoolId/parent-invites/import"
            element={
              <RequireAuth>
                <ParentInviteImportPage />
              </RequireAuth>
            }
          />
          <Route
            path="/teams"
            element={
              <RequireAuth>
                <TeamsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/schools/:schoolId/teams/new"
            element={
              <RequireAuth>
                <TeamCreatePage />
              </RequireAuth>
            }
          />
          <Route
            path="/teams/:id/edit"
            element={
              <RequireAuth>
                <TeamEditPage />
              </RequireAuth>
            }
          />
          <Route
            path="/teams/:teamId/positions"
            element={
              <RequireAuth>
                <TeamPositionsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/teams/:id"
            element={
              <RequireAuth>
                <TeamDetailPage />
              </RequireAuth>
            }
          />
          <Route
            path="/age-groups/:id"
            element={
              <RequireAuth>
                <AgeGroupDetailPage />
              </RequireAuth>
            }
          />
          <Route
            path="/age-groups/:id/edit"
            element={
              <RequireAuth>
                <AgeGroupEditPage />
              </RequireAuth>
            }
          />
          <Route
            path="/my-children"
            element={
              <RequireAuth>
                <MyChildrenPage />
              </RequireAuth>
            }
          />
          {/* 186: mobile-only "More" menu (bottom-tab destination); harmless on
              desktop, where the sidebar provides the full nav. */}
          <Route
            path="/more"
            element={
              <RequireAuth>
                <MoreScreen />
              </RequireAuth>
            }
          />
          <Route
            path="/me/player-home"
            element={
              <RequireAuth>
                <PlayerHomePage />
              </RequireAuth>
            }
          />
          <Route
            path="/my-events"
            element={
              <RequireAuth>
                <MyEventsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/messages"
            element={
              <RequireAuth>
                <MessagesPage />
              </RequireAuth>
            }
          />
          <Route
            path="/inbox"
            element={
              <RequireAuth>
                <InboxPage />
              </RequireAuth>
            }
          />
          <Route
            path="/notifications"
            element={
              <RequireAuth>
                <NotificationsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/court-templates"
            element={
              <RequireAuth>
                <CourtTemplatesPage />
              </RequireAuth>
            }
          />
          <Route
            path="/court-types/:courtTypeId/templates/new"
            element={
              <RequireAuth>
                <CourtTemplateCreatePage />
              </RequireAuth>
            }
          />
          <Route
            path="/court-templates/:id/edit"
            element={
              <RequireAuth>
                <CourtTemplateEditPage />
              </RequireAuth>
            }
          />
          <Route
            path="/court-templates/:id"
            element={
              <RequireAuth>
                <CourtTemplateDetailPage />
              </RequireAuth>
            }
          />
          <Route
            path="/asset-types"
            element={
              <RequireAuth>
                <AssetTypesPage />
              </RequireAuth>
            }
          />
          <Route
            path="/asset-types/new"
            element={
              <RequireAuth>
                <AssetTypeCreatePage />
              </RequireAuth>
            }
          />
          <Route
            path="/asset-types/:id/edit"
            element={
              <RequireAuth>
                <AssetTypeEditPage />
              </RequireAuth>
            }
          />
          <Route
            path="/asset-types/:id"
            element={
              <RequireAuth>
                <AssetTypeDetailPage />
              </RequireAuth>
            }
          />
          <Route
            path="/packages"
            element={
              <RequireAuth>
                <PackagesPage />
              </RequireAuth>
            }
          />
          <Route
            path="/packages/new"
            element={
              <RequireAuth>
                <PackageCreatePage />
              </RequireAuth>
            }
          />
          <Route
            path="/packages/:id/edit"
            element={
              <RequireAuth>
                <PackageEditPage />
              </RequireAuth>
            }
          />
          <Route
            path="/packages/:id"
            element={
              <RequireAuth>
                <PackageDetailPage />
              </RequireAuth>
            }
          />
          <Route
            path="/sponsors"
            element={
              <RequireAuth>
                <SponsorsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/sponsors/new"
            element={
              <RequireAuth>
                <SponsorCreatePage />
              </RequireAuth>
            }
          />
          <Route
            path="/sponsors/:id/edit"
            element={
              <RequireAuth>
                <SponsorEditPage />
              </RequireAuth>
            }
          />
          <Route
            path="/sponsors/:id"
            element={
              <RequireAuth>
                <SponsorDetailPage />
              </RequireAuth>
            }
          />
          <Route
            path="/my-sponsor/dashboard"
            element={
              <RequireAuth>
                <SponsorDashboardPage />
              </RequireAuth>
            }
          />
          <Route
            path="/my-sponsor/users"
            element={
              <RequireAuth>
                <SponsorUsersPage />
              </RequireAuth>
            }
          />
          <Route
            path="/sponsors/:sponsorId/offers"
            element={
              <RequireAuth>
                <SponsorOffersPage />
              </RequireAuth>
            }
          />
          {/* 090 Shop */}
          <Route
            path="/shop"
            element={
              <RequireAuth>
                <ShopPage />
              </RequireAuth>
            }
          />
          <Route
            path="/shop/:slug"
            element={
              <RequireAuth>
                <ShopProviderPage />
              </RequireAuth>
            }
          />
          <Route
            path="/shop/:slug/cart"
            element={
              <RequireAuth>
                <ShopCartPage />
              </RequireAuth>
            }
          />
          {/* 096 -- Cashback */}
          <Route
            path="/shop/:slug/link/mock"
            element={
              <RequireAuth>
                <ShopLinkMockPage />
              </RequireAuth>
            }
          />
          <Route
            path="/me/cashback"
            element={
              <RequireAuth>
                <MyCashbackPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/shop/providers/:id/cashback"
            element={
              <RequireAuth>
                <AdminCashbackPage />
              </RequireAuth>
            }
          />
          {/* 097 -- Volunteer Coordination */}
          <Route
            path="/me/volunteer-offers"
            element={
              <RequireAuth>
                <MyVolunteerOffersPage />
              </RequireAuth>
            }
          />
          <Route
            path="/schools/:id/volunteers"
            element={
              <RequireAuth>
                <SchoolVolunteerDirectoryPage />
              </RequireAuth>
            }
          />
          <Route
            path="/me/orders"
            element={
              <RequireAuth>
                <MyOrdersPage />
              </RequireAuth>
            }
          />
          <Route
            path="/me/orders/:slug"
            element={
              <RequireAuth>
                <MyOrderDetailPage />
              </RequireAuth>
            }
          />
          <Route
            path="/placements"
            element={
              <RequireAuth>
                <PlacementsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/placements/new"
            element={
              <RequireAuth>
                <PlacementCreatePage />
              </RequireAuth>
            }
          />
          <Route
            path="/placements/:id/edit"
            element={
              <RequireAuth>
                <PlacementEditPage />
              </RequireAuth>
            }
          />
          <Route
            path="/placements/:id"
            element={
              <RequireAuth>
                <PlacementDetailPage />
              </RequireAuth>
            }
          />
          <Route
            path="/schools/:schoolId/calendar"
            element={
              <RequireAuth>
                <CalendarPage />
              </RequireAuth>
            }
          />
          <Route
            path="/events"
            element={
              <RequireAuth>
                <EventsGlobalPage />
              </RequireAuth>
            }
          />
          <Route
            path="/discover"
            element={
              <PublicOrAuth>
                <DiscoverPage />
              </PublicOrAuth>
            }
          />
          <Route
            path="/following"
            element={
              <PublicOrAuth>
                <FollowingPage />
              </PublicOrAuth>
            }
          />
          <Route
            path="/live"
            element={
              <PublicOrAuth>
                <LivePage />
              </PublicOrAuth>
            }
          />
          <Route
            path="/profile"
            element={
              <RequireAuth>
                <ProfilePage />
              </RequireAuth>
            }
          />
          <Route
            path="/settings"
            element={
              <RequireAuth>
                <SettingsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/emails"
            element={
              <RequireAuth>
                <AdminEmailsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/policies"
            element={
              <RequireAuth>
                <AdminPoliciesPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/pending-venues"
            element={
              <RequireAuth>
                <AdminPendingVenuesPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/shop/providers"
            element={
              <RequireAuth>
                <AdminShopProvidersPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/venues"
            element={
              <RequireAuth>
                <AdminVenuesPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/age-groups"
            element={
              <RequireAuth>
                <AdminAgeGroupsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/sports"
            element={
              <RequireAuth>
                <AdminSportsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/shop/providers/new"
            element={
              <RequireAuth>
                <AdminShopProviderEditPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/shop/providers/:id/edit"
            element={
              <RequireAuth>
                <AdminShopProviderEditPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/shop/providers/:id/products"
            element={
              <RequireAuth>
                <AdminShopProductsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/shop/orders"
            element={
              <RequireAuth>
                <AdminShopOrdersPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/shop/orders/:slug"
            element={
              <RequireAuth>
                <AdminShopOrderDetailPage />
              </RequireAuth>
            }
          />
          <Route
            path="/onboarding"
            element={
              <RequireAuth>
                <OnboardingPage />
              </RequireAuth>
            }
          />
          <Route
            path="/preferences"
            element={
              <RequireAuth>
                <PreferencesPage />
              </RequireAuth>
            }
          />
          <Route
            path="/schools/:schoolId/events"
            element={
              <RequireAuth>
                <EventsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/schools/:schoolId/events/new"
            element={
              <RequireAuth>
                <EventCreatePage />
              </RequireAuth>
            }
          />
          <Route
            path="/schools/:schoolId/match-day-templates"
            element={
              <RequireAuth>
                <MatchDayTemplatesPage />
              </RequireAuth>
            }
          />
          <Route
            path="/events/:id/edit"
            element={
              <RequireAuth>
                <EventEditPage />
              </RequireAuth>
            }
          />
          <Route
            path="/events/:id/tabs/:tab"
            element={
              <RequireAuth>
                <EventTabsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/events/:id/tabs"
            element={
              <RequireAuth>
                <EventTabsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/schools/:schoolId/practice-series"
            element={
              <RequireAuth>
                <PracticeSeriesPage />
              </RequireAuth>
            }
          />
          <Route
            path="/events/:id/tournament"
            element={
              <PublicOrAuth>
                <TournamentPage />
              </PublicOrAuth>
            }
          />
          <Route
            path="/fixture-invites"
            element={
              <RequireAuth>
                <FixtureInvitesPage />
              </RequireAuth>
            }
          />
          <Route
            path="/events/:id"
            element={
              <RequireAuth>
                <EventDetailPage />
              </RequireAuth>
            }
          />
          <Route
            path="/events/:eventId/matches/new"
            element={
              <RequireAuth>
                <MatchCreatePage />
              </RequireAuth>
            }
          />
          <Route
            path="/matches/:id/edit"
            element={
              <RequireAuth>
                <MatchEditPage />
              </RequireAuth>
            }
          />
          <Route
            path="/matches/:matchId/teams/:teamId/lineup"
            element={
              <RequireAuth>
                <MatchLineupPage />
              </RequireAuth>
            }
          />
          <Route
            path="/matches/:matchId/boksmart"
            element={
              <RequireAuth>
                <BokSmartFormPage />
              </RequireAuth>
            }
          />
          <Route
            path="/rugby/matches/:id/score"
            element={
              <RequireAuth>
                <RugbyMatchScorePage />
              </RequireAuth>
            }
          />
          <Route
            path="/rugby-sevens/matches/:id/score"
            element={
              <RequireAuth>
                <RugbySevensMatchScorePage />
              </RequireAuth>
            }
          />
          <Route
            path="/netball/matches/:id/score"
            element={
              <RequireAuth>
                <NetballMatchScorePage />
              </RequireAuth>
            }
          />
          <Route
            path="/tennis/matches/:id/score"
            element={
              <RequireAuth>
                <TennisMatchScorePage />
              </RequireAuth>
            }
          />
          <Route
            path="/cricket/matches/:id/score"
            element={
              <RequireAuth>
                <CricketMatchScorePage />
              </RequireAuth>
            }
          />
          <Route
            path="/athletics/matches/:id/score"
            element={
              <RequireAuth>
                <AthleticsMatchScorePage />
              </RequireAuth>
            }
          />
          <Route
            path="/decathlon/matches/:id/score"
            element={
              <RequireAuth>
                <DecathlonMatchScorePage />
              </RequireAuth>
            }
          />
          <Route
            path="/schools/:schoolId/rugby/league-table"
            element={
              <RequireAuth>
                <RugbyLeagueTablePage />
              </RequireAuth>
            }
          />
          <Route
            path="/schools/:schoolId/netball/league-table"
            element={
              <RequireAuth>
                <NetballLeagueTablePage />
              </RequireAuth>
            }
          />
          <Route
            path="/matches/:id"
            element={
              <RequireAuth>
                <MatchDetailPage />
              </RequireAuth>
            }
          />
          <Route
            path="/courts"
            element={
              <RequireAuth>
                <CourtsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/schools/:schoolId/courts/new"
            element={
              <RequireAuth>
                <CourtCreatePage />
              </RequireAuth>
            }
          />
          <Route
            path="/courts/:id/edit"
            element={
              <RequireAuth>
                <CourtEditPage />
              </RequireAuth>
            }
          />
          <Route
            path="/courts/:id/3d"
            element={
              <RequireAuth>
                <Suspense
                  fallback={
                    <p className="text-muted-foreground">
                      Loading 3D viewer...
                    </p>
                  }
                >
                  <Field3DPage />
                </Suspense>
              </RequireAuth>
            }
          />
          <Route
            path="/courts/:id"
            element={
              <RequireAuth>
                <CourtDetailPage />
              </RequireAuth>
            }
          />
          <Route
            path="/assets/:id"
            element={
              <RequireAuth>
                <AssetDetailPage />
              </RequireAuth>
            }
          />
          <Route
            path="/zones"
            element={
              <RequireAuth>
                <ZonesPage />
              </RequireAuth>
            }
          />
          <Route
            path="/schools/:schoolId/zones/new"
            element={
              <RequireAuth>
                <ZoneCreatePage />
              </RequireAuth>
            }
          />
          <Route
            path="/zones/:id/edit"
            element={
              <RequireAuth>
                <ZoneEditPage />
              </RequireAuth>
            }
          />
          <Route
            path="/live-coaches"
            element={
              <RequireAuth>
                <LiveCoachesPage />
              </RequireAuth>
            }
          />
        </Routes>
        </UserScope>
        </ImpersonationProvider>
      </AuthProvider>
    </BrowserRouter>
    </Sentry.ErrorBoundary>
  );
}
